import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation';
import { useTranslation } from 'react-i18next';
import { DesktopIcon } from '../../icons/DesktopIcon';
import { MobileIcon } from '../../icons/MobileIcon';
import { TVIcon } from '../../icons/TVIcon';

type DeviceDistributionIcon = 'mobile' | 'tv' | 'desktop';
type DeviceDistributionColor = 'charts.indigo' | 'charts.orchid' | 'charts.lightBlue';

export type DeviceDistributionItem = {
  dataTest: string;
  icon: DeviceDistributionIcon;
  title: string;
  hint?: string;
  value: string;
};

const deviceDistributionIcons: Record<DeviceDistributionIcon, React.ReactNode> = {
  mobile: <MobileIcon />,
  tv: <TVIcon />,
  desktop: <DesktopIcon />,
};

const deviceDistributionColors: Record<DeviceDistributionIcon, DeviceDistributionColor> = {
  mobile: 'charts.indigo',
  tv: 'charts.orchid',
  desktop: 'charts.lightBlue',
};

type DeviceDistributionProps = {
  items: DeviceDistributionItem[];
};

export const DeviceDistribution = ({ items }: DeviceDistributionProps) => {
  const { t } = useTranslation();

  return (
    <Card
      data-test="audience-details-device-distribution"
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '4px',
        boxShadow: ELEVATION.elevation0,
        flex: 1,
      }}
    >
      <CardContent sx={{ p: 1.5 }}>
        <Typography variant="body1Bold">{t('Device Distribution')}</Typography>

        <Stack
          direction="column"
          sx={{
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            gap: 2,
          }}
        >
          {items.map(item => (
            <Stack
              key={item.dataTest}
              data-test={item.dataTest}
              direction="row"
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 1.5,
                height: '64px',
                paddingX: 2,
                paddingY: 0.5,
                width: '100%',
                borderRadius: '10px',
                backgroundColor: 'background.boxLight',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: deviceDistributionColors[item.icon],
                }}
              >
                {deviceDistributionIcons[item.icon]}
              </Box>

              <Stack
                direction="column"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {item.hint}
                  </Typography>
                </Stack>

                <Typography variant="h2" sx={{ color: 'text.secondary' }}>
                  {item.value}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
